import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StatusBadge from "@/components/common/StatusBadge";
import { formatEuro, formatNumber } from "@/lib/format";
import { Plus, Trash2, Copy, Layers, DoorOpen } from "lucide-react";
import { toast } from "sonner";
import {
  BUILDING_TYPE_LABELS, OCCUPANCY_STATUS_LABELS, OCCUPANCY_STATUS_TONES,
} from "@/constants/patrimoine";

const EMPTY_LOT = {
  reference: "", lot_type: "appartement", floor: "", surface_m2: "", rooms: "",
  destination: "habitation", target_rent: "", planned_charges: "", tantiemes: "",
  occupancy_status: "vacant", parent_lot_id: "none", meter_type: "individual",
};

export default function BuildingLotsTab({ building }) {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_LOT });
  const [bulk, setBulk] = useState({ ...EMPTY_LOT, count: 5, reference_prefix: "Logement" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/lots?building_id=${building.id}`);
      setLots(data.items || []);
    } catch (err) {
      console.error("Load lots failed:", err);
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [building.id]);

  const create = async () => {
    try {
      const payload = { ...form, building_id: building.id };
      ["surface_m2", "target_rent", "planned_charges", "tantiemes", "rooms"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
        else payload[k] = k === "rooms" ? parseInt(payload[k]) : parseFloat(payload[k]);
      });
      if (payload.parent_lot_id === "none" || !payload.parent_lot_id) delete payload.parent_lot_id;
      await api.post("/lots", payload);
      toast.success("Lot créé"); setOpen(false); setForm({ ...EMPTY_LOT }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Création impossible"); }
  };

  const bulkCreate = async () => {
    try {
      const template = { ...bulk }; delete template.count; delete template.reference_prefix;
      template.building_id = building.id;
      ["surface_m2", "target_rent", "planned_charges", "tantiemes", "rooms"].forEach((k) => {
        if (template[k] === "" || template[k] == null) delete template[k];
        else template[k] = k === "rooms" ? parseInt(template[k]) : parseFloat(template[k]);
      });
      template.reference = "Placeholder"; // required by schema; overwritten by backend
      if (template.parent_lot_id === "none" || !template.parent_lot_id) delete template.parent_lot_id;
      await api.post(`/buildings/${building.id}/lots/bulk`,
                     { template, count: parseInt(bulk.count), reference_prefix: bulk.reference_prefix });
      toast.success(`${bulk.count} lots créés`); setBulkOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Création groupée impossible"); }
  };

  const duplicate = async (lotId) => {
    try { await api.post(`/lots/${lotId}/duplicate`); toast.success("Lot dupliqué"); load(); }
    catch { toast.error("Duplication impossible"); }
  };

  const del = async (lotId) => {
    if (!window.confirm("Supprimer ce lot ?")) return;
    try { await api.delete(`/lots/${lotId}`); toast.success("Lot supprimé"); load(); }
    catch { toast.error("Suppression impossible"); }
  };

  const parents = lots.filter((l) => !l.parent_lot_id);
  const childrenByParent = lots.reduce((acc, l) => {
    if (l.parent_lot_id) { (acc[l.parent_lot_id] = acc[l.parent_lot_id] || []).push(l); }
    return acc;
  }, {});
  const orphans = lots.filter((l) => l.parent_lot_id && !lots.find((p) => p.id === l.parent_lot_id));
  const displayed = [...parents, ...orphans];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{lots.length} lot{lots.length > 1 ? "s" : ""} sur ce bâtiment</div>
        <div className="flex gap-2">
          <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="btn-pill" data-testid="lots-bulk-btn">
                <Layers className="mr-2 h-4 w-4" /> Créer plusieurs lots
              </Button>
            </DialogTrigger>
            <BulkLotsDialog bulk={bulk} setBulk={setBulk} onSubmit={bulkCreate} onCancel={() => setBulkOpen(false)} />
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-pill" data-testid="lot-add-btn"><Plus className="mr-2 h-4 w-4" /> Nouveau lot</Button>
            </DialogTrigger>
            <LotDialog form={form} setForm={setForm} onSubmit={create} onCancel={() => setOpen(false)} parents={parents} />
          </Dialog>
        </div>
      </div>

      <div className="card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40">
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-6 py-4">Lot</th>
              <th className="px-6 py-4">Type</th>
              <th className="px-6 py-4 text-right">Surface</th>
              <th className="px-6 py-4 text-right">Loyer cible</th>
              <th className="px-6 py-4 text-right">Charges</th>
              <th className="px-6 py-4">Occupation</th>
              <th className="px-6 py-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && <tr><td colSpan={7} className="px-6 py-8 text-center text-muted-foreground">Chargement…</td></tr>}
            {!loading && lots.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-12 text-center text-muted-foreground">
                <DoorOpen className="mx-auto h-6 w-6 mb-2 text-muted-foreground/60" /> Aucun lot.
              </td></tr>
            )}
            {displayed.map((l) => (
              <React.Fragment key={l.id}>
                <LotRow lot={l} onDuplicate={duplicate} onDelete={del} />
                {(childrenByParent[l.id] || []).map((child) => (
                  <LotRow key={child.id} lot={child} onDuplicate={duplicate} onDelete={del} indent />
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LotRow({ lot, onDuplicate, onDelete, indent }) {
  return (
    <tr data-testid={`lot-row-${lot.id}`} className={`hover:bg-secondary/30 ${indent ? "bg-secondary/20" : ""}`}>
      <td className="px-6 py-3">
        <div className={`font-medium ${indent ? "pl-6 border-l-2 border-accent/40" : ""}`}>{lot.reference}</div>
        {lot.destination && <div className={`text-xs text-muted-foreground italic ${indent ? "pl-6" : ""}`}>{lot.destination}</div>}
      </td>
      <td className="px-6 py-3 text-xs">{BUILDING_TYPE_LABELS[lot.lot_type] || lot.lot_type}</td>
      <td className="px-6 py-3 text-right font-mono">{lot.surface_m2 ? `${formatNumber(lot.surface_m2)} m²` : "—"}</td>
      <td className="px-6 py-3 text-right font-mono">{formatEuro(lot.target_rent || lot.monthly_rent)}</td>
      <td className="px-6 py-3 text-right font-mono text-xs text-muted-foreground">{formatEuro(lot.planned_charges || lot.monthly_charges)}</td>
      <td className="px-6 py-3">
        <StatusBadge tone={OCCUPANCY_STATUS_TONES[lot.occupancy_status] || (lot.is_occupied ? "success" : "warning")}>
          {OCCUPANCY_STATUS_LABELS[lot.occupancy_status] || (lot.is_occupied ? "Occupé" : "Vacant")}
        </StatusBadge>
      </td>
      <td className="px-6 py-3 text-right space-x-1">
        <Button variant="ghost" size="sm" onClick={() => onDuplicate(lot.id)} title="Dupliquer"><Copy className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(lot.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
      </td>
    </tr>
  );
}

function LotDialog({ form, setForm, onSubmit, onCancel, parents }) {
  return (
    <DialogContent className="max-w-xl">
      <DialogHeader><DialogTitle>Nouveau lot</DialogTitle></DialogHeader>
      <div className="space-y-4 py-2">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Référence</Label>
            <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="lot-form-reference" required />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.lot_type} onValueChange={(v) => setForm({ ...form, lot_type: v })}>
              <SelectTrigger data-testid="lot-form-type"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(BUILDING_TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Étage</Label><Input value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} /></div>
          <div><Label>Surface (m²)</Label><Input type="number" value={form.surface_m2} onChange={(e) => setForm({ ...form, surface_m2: e.target.value })} /></div>
          <div><Label>Pièces</Label><Input type="number" value={form.rooms} onChange={(e) => setForm({ ...form, rooms: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Loyer cible</Label><Input type="number" value={form.target_rent} onChange={(e) => setForm({ ...form, target_rent: e.target.value })} data-testid="lot-form-rent" /></div>
          <div><Label>Charges prévues</Label><Input type="number" value={form.planned_charges} onChange={(e) => setForm({ ...form, planned_charges: e.target.value })} /></div>
          <div><Label>Tantièmes</Label><Input type="number" value={form.tantiemes} onChange={(e) => setForm({ ...form, tantiemes: e.target.value })} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Occupation</Label>
            <Select value={form.occupancy_status} onValueChange={(v) => setForm({ ...form, occupancy_status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(OCCUPANCY_STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Lot parent (facultatif)</Label>
            <Select value={form.parent_lot_id || "none"} onValueChange={(v) => setForm({ ...form, parent_lot_id: v })}>
              <SelectTrigger data-testid="lot-form-parent"><SelectValue placeholder="Aucun" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun</SelectItem>
                {parents.map((p) => <SelectItem key={p.id} value={p.id}>{p.reference}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div><Label>Destination</Label><Input value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} placeholder="habitation, commercial, logistique…" /></div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>Annuler</Button>
        <Button onClick={onSubmit} data-testid="lot-form-submit">Créer le lot</Button>
      </DialogFooter>
    </DialogContent>
  );
}

function BulkLotsDialog({ bulk, setBulk, onSubmit, onCancel }) {
  return (
    <DialogContent>
      <DialogHeader><DialogTitle>Créer plusieurs lots</DialogTitle></DialogHeader>
      <div className="space-y-4 py-2">
        <p className="text-sm text-muted-foreground">
          Chaque lot sera créé à partir du même gabarit. Les références seront <em>Préfixe 1</em>, <em>Préfixe 2</em>…
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Nombre de lots</Label><Input type="number" min={1} max={50} value={bulk.count} onChange={(e) => setBulk({ ...bulk, count: e.target.value })} data-testid="lots-bulk-count" /></div>
          <div><Label>Préfixe</Label><Input value={bulk.reference_prefix} onChange={(e) => setBulk({ ...bulk, reference_prefix: e.target.value })} data-testid="lots-bulk-prefix" /></div>
        </div>
        <div>
          <Label>Type</Label>
          <Select value={bulk.lot_type} onValueChange={(v) => setBulk({ ...bulk, lot_type: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(BUILDING_TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Surface (m²)</Label><Input type="number" value={bulk.surface_m2} onChange={(e) => setBulk({ ...bulk, surface_m2: e.target.value })} /></div>
          <div><Label>Loyer cible (€)</Label><Input type="number" value={bulk.target_rent} onChange={(e) => setBulk({ ...bulk, target_rent: e.target.value })} /></div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>Annuler</Button>
        <Button onClick={onSubmit} data-testid="lots-bulk-submit">Créer les lots</Button>
      </DialogFooter>
    </DialogContent>
  );
}
